import { Permission } from '@procureai/permissions';
import { RestrictionCheck, useFeatureFlags, usePermissions, validateRestriction } from '@procureai/permissions-ui';
import { find, isNotNil } from 'ramda';

import { SvbrgAgentId } from '@/generated/gql/graphql';
import { FeatureFlag } from '@/legacy/common/types';

type AgentIdRestrictionCheck = { agentId: SvbrgAgentId; restrictionCheck: RestrictionCheck };

const featureBasedFrontdeskAssistant: AgentIdRestrictionCheck[] = [
  {
    agentId: SvbrgAgentId.buyerAssistant,
    restrictionCheck: {
      feature: FeatureFlag.ENABLE_ENBW_SMART_SCALE_AGENT,
      permission: Permission.AgentBuyerUse,
    },
  },
  {
    agentId: SvbrgAgentId.requesterAssistant,
    restrictionCheck: {
      feature: FeatureFlag.ENABLE_ENBW_SMART_SCALE_AGENT,
      permission: Permission.AgentRequesterUse,
    },
  },
];

/**
 * Hook to get the appropriate frontdesk assistant agent ID based on the user role (and additional feature flags set).
 *
 * Using the non-hook related validateRestriction with `usePermissions`and `useFeatureFlags` hooks to specifically allow iterating over the
 * configuration for more specific feature-based frontdesk assistants. Falling back to generic frontdesk agent if no specific assistant matches.
 */
export const useFrontdeskAssistant = () => {
  const { hasFeatureFlag } = useFeatureFlags();
  const { hasPermission } = usePermissions();

  const specificAssistant = find(
    ({ restrictionCheck }) => validateRestriction(restrictionCheck, hasPermission, hasFeatureFlag),
    featureBasedFrontdeskAssistant,
  );

  return isNotNil(specificAssistant) ? specificAssistant.agentId : SvbrgAgentId.frontdeskAgent;
};
